import React, { useState, useEffect, useCallback } from "react";
import Layout from "./Layout";
import { DataTable } from "./common/DataTable";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Squares2X2Icon,
  PlusIcon,
  Cog6ToothIcon,
  ArrowsPointingOutIcon,
  DocumentArrowDownIcon,
  ShareIcon,
  BookmarkIcon,
  FunnelIcon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  CheckIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  DocumentTextIcon,
  BellIcon,
  FireIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

// Recharts for comprehensive data visualization
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
} from "recharts";

const InteractiveDashboard = () => {
  const { user } = useAuth();
  
  // State for dashboard management
  const [dashboardLayout, setDashboardLayout] = useState([]);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [dashboardConfig, setDashboardConfig] = useState({
    name: "My Dashboard",
    columns: 4,
    autoRefresh: 300, // 5 minutes
    theme: "light"
  });
  
  // State for widget management
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);
  
  // State for filters and controls
  const [globalFilters, setGlobalFilters] = useState({
    timeframe: "30d",
    refreshInterval: 300,
    framework: "all",
    department: "all"
  });
  
  // Get backend URL
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Widget library definitions inspired by Klipfolio, Databox, Geckoboard
  const widgetLibrary = [
    {
      id: "compliance_score_card",
      name: "Compliance Score Card",
      category: "KPI",
      icon: ShieldCheckIcon,
      type: "kpi_card",
      size: { width: 1, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/compliance-score",
      description: "Overall compliance percentage with trend"
    },
    {
      id: "risk_overview_card",
      name: "Risk Overview",
      category: "KPI", 
      icon: ExclamationTriangleIcon,
      type: "kpi_card",
      size: { width: 1, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/risk-overview",
      description: "Total risks with severity breakdown"
    },
    {
      id: "system_health_gauge",
      name: "System Health Gauge",
      category: "Gauge",
      icon: FireIcon,
      type: "gauge",
      size: { width: 1, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/system-health",
      description: "System uptime and performance gauge"
    },
    {
      id: "compliance_trend_chart",
      name: "Compliance Trend",
      category: "Chart",
      icon: ChartBarIcon,
      type: "line_chart",
      size: { width: 2, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/compliance-trend",
      description: "6-month compliance score trend"
    },
    {
      id: "risk_heatmap",
      name: "Risk Heatmap",
      category: "Chart",
      icon: Squares2X2Icon,
      type: "heatmap",
      size: { width: 2, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/risk-heatmap",
      description: "Risk impact vs likelihood matrix"
    },
    {
      id: "alerts_table",
      name: "Active Alerts",
      category: "Table",
      icon: BellIcon,
      type: "table",
      size: { width: 2, height: 2 },
      apiEndpoint: "/api/dashboard/widgets/alerts-table",
      description: "Real-time security and compliance alerts"
    },
    {
      id: "vulnerability_summary",
      name: "Vulnerability Summary",
      category: "KPI",
      icon: ShieldCheckIcon,
      type: "kpi_card",
      size: { width: 1, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/vulnerability-summary",
      description: "Critical vulnerabilities count"
    },
    {
      id: "tprm_vendor_status",
      name: "Vendor Risk Status",
      category: "Chart",
      icon: BuildingOfficeIcon,
      type: "donut_chart",
      size: { width: 1, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/vendor-risk-status",
      description: "Third-party vendor risk distribution"
    },
    {
      id: "team_performance",
      name: "Team Performance",
      category: "KPI",
      icon: UsersIcon,
      type: "progress_bar",
      size: { width: 2, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/team-performance",
      description: "Team collaboration metrics"
    },
    {
      id: "training_completion",
      name: "Training Completion",
      category: "Chart",
      icon: AcademicCapIcon,
      type: "bar_chart",
      size: { width: 2, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/training-completion",
      description: "Training completion rates by department"
    },
    {
      id: "cspm_cloud_assets",
      name: "Cloud Security Status",
      category: "KPI",
      icon: CircleStackIcon,
      type: "kpi_card",
      size: { width: 1, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/cloud-security-status",
      description: "Cloud assets security posture"
    },
    {
      id: "dspm_data_classification",
      name: "Data Classification",
      category: "Chart",
      icon: CircleStackIcon,
      type: "pie_chart",
      size: { width: 1, height: 1 },
      apiEndpoint: "/api/dashboard/widgets/data-classification",
      description: "Sensitive data classification breakdown"
    }
  ];

  useEffect(() => {
    initializeDashboard();
  }, []);

  useEffect(() => {
    if (selectedWidgets.length > 0) {
      fetchAllWidgetData();
      const interval = setInterval(fetchAllWidgetData, globalFilters.refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [selectedWidgets, globalFilters.refreshInterval]);

  const initializeDashboard = async () => {
    setLoading(true);
    setAvailableWidgets(widgetLibrary);
    
    // Load saved dashboard layout or create default
    const savedLayout = localStorage.getItem(`dashboard_${user?.id}`);
    if (savedLayout) {
      const layout = JSON.parse(savedLayout);
      setSelectedWidgets(layout.widgets || []);
      setDashboardConfig(layout.config || dashboardConfig);
    } else {
      // Create default dashboard based on user role
      const defaultWidgets = getDefaultWidgetsForRole(user?.role);
      setSelectedWidgets(defaultWidgets);
    }
    
    setLoading(false);
  };

  const getDefaultWidgetsForRole = (role) => {
    const createWidget = (widgetTemplate, position) => ({
      ...widgetTemplate,
      id: `${widgetTemplate.id}_${Date.now()}_${Math.random()}`,
      position
    });

    const baseWidgets = [
      createWidget(widgetLibrary[0], { x: 0, y: 0 }), // Compliance Score
      createWidget(widgetLibrary[1], { x: 1, y: 0 }), // Risk Overview
      createWidget(widgetLibrary[2], { x: 2, y: 0 }), // System Health
      createWidget(widgetLibrary[3], { x: 0, y: 1 })  // Compliance Trend
    ];

    if (role === "admin") {
      return [
        ...baseWidgets,
        createWidget(widgetLibrary[5], { x: 2, y: 1 }), // Alerts Table
        createWidget(widgetLibrary[7], { x: 0, y: 2 }), // Vendor Status
        createWidget(widgetLibrary[9], { x: 1, y: 2 })  // Training Completion
      ];
    } else if (role === "auditor") {
      return [
        ...baseWidgets,
        createWidget(widgetLibrary[4], { x: 2, y: 1 }), // Risk Heatmap
        createWidget(widgetLibrary[11], { x: 0, y: 2 }) // Data Classification
      ];
    } else {
      return baseWidgets;
    }
  };

  const fetchAllWidgetData = async () => {
    const token = localStorage.getItem('token');
    const dataPromises = selectedWidgets.map(async (widget) => {
      try {
        const response = await fetch(`${backendUrl}${widget.apiEndpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': user?.tenant_id
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          return { widgetId: widget.id, data: data.success ? data.data : data };
        }
        return { widgetId: widget.id, data: null };
      } catch (error) {
        console.error(`Error fetching data for widget ${widget.id}:`, error);
        return { widgetId: widget.id, data: null };
      }
    });

    const results = await Promise.all(dataPromises);
    const newWidgetData = {};
    results.forEach(result => {
      newWidgetData[result.widgetId] = result.data;
    });
    
    setWidgetData(newWidgetData);
  };

  const addWidgetToDashboard = (widget) => {
    const newWidget = {
      ...widget,
      id: `${widget.id}_${Date.now()}`,
      position: findEmptyPosition()
    };
    
    setSelectedWidgets(prev => [...prev, newWidget]);
    setShowWidgetLibrary(false);
  };

  const findEmptyPosition = () => {
    const occupied = selectedWidgets.map(w => w.position);
    for (let y = 0; y < 10; y++) {
      for (let x = 0; x < dashboardConfig.columns; x++) {
        if (!occupied.some(pos => pos.x === x && pos.y === y)) {
          return { x, y };
        }
      }
    }
    return { x: 0, y: selectedWidgets.length };
  };

  const removeWidget = (widgetId) => {
    setSelectedWidgets(prev => prev.filter(w => w.id !== widgetId));
  };

  const saveDashboard = () => {
    const dashboardData = {
      widgets: selectedWidgets,
      config: dashboardConfig,
      lastSaved: new Date().toISOString()
    };
    
    localStorage.setItem(`dashboard_${user?.id}`, JSON.stringify(dashboardData));
    
    // Show success toast (you can implement this)
    console.log("Dashboard saved successfully!");
  };

  const exportDashboard = () => {
    const exportData = {
      widgets: selectedWidgets,
      config: dashboardConfig,
      exportedAt: new Date().toISOString(),
      exportedBy: user?.full_name
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_${dashboardConfig.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderWidget = (widget) => {
    const data = widgetData[widget.id];
    
    switch (widget.type) {
      case "kpi_card":
        return <KPICard widget={widget} data={data} />;
      case "line_chart":
        return <LineChartWidget widget={widget} data={data} />;
      case "bar_chart":
        return <BarChartWidget widget={widget} data={data} />;
      case "pie_chart":
        return <PieChartWidget widget={widget} data={data} />;
      case "donut_chart":
        return <DonutChartWidget widget={widget} data={data} />;
      case "gauge":
        return <GaugeWidget widget={widget} data={data} />;
      case "table":
        return <TableWidget widget={widget} data={data} />;
      case "heatmap":
        return <HeatmapWidget widget={widget} data={data} />;
      case "progress_bar":
        return <ProgressBarWidget widget={widget} data={data} />;
      default:
        return <div className="p-4 text-center text-gray-500">Widget type not supported</div>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Dashboard Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{dashboardConfig.name}</h1>
            <p className="text-gray-600 mt-1">Interactive Analytics Dashboard</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Global Filters */}
            <select
              value={globalFilters.timeframe}
              onChange={(e) => setGlobalFilters(prev => ({ ...prev, timeframe: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
            
            {/* Auto-refresh control */}
            <select
              value={globalFilters.refreshInterval}
              onChange={(e) => setGlobalFilters(prev => ({ ...prev, refreshInterval: parseInt(e.target.value) }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={60}>1 minute</option>
              <option value={300}>5 minutes</option>
              <option value={900}>15 minutes</option>
              <option value={3600}>1 hour</option>
            </select>
            
            {/* Dashboard controls */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowWidgetLibrary(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Widget
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setEditMode(!editMode)}
              className={`flex items-center px-4 py-2 rounded-lg ${editMode ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              {editMode ? <CheckIcon className="w-4 h-4 mr-2" /> : <PencilIcon className="w-4 h-4 mr-2" />}
              {editMode ? 'Done' : 'Edit'}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={saveDashboard}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <BookmarkIcon className="w-4 h-4 mr-2" />
              Save
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={exportDashboard}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export
            </motion.button>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className={`grid gap-6 ${dashboardConfig.columns === 4 ? 'grid-cols-4' : dashboardConfig.columns === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {selectedWidgets.map((widget) => (
            <motion.div
              key={widget.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={editMode ? { scale: 1.02 } : {}}
              className={`bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden ${
                widget.size.width === 2 ? 'col-span-2' : ''
              } ${widget.size.height === 2 ? 'row-span-2' : ''} ${
                editMode ? 'ring-2 ring-blue-300' : ''
              }`}
            >
              {editMode && (
                <div className="absolute top-2 right-2 z-10 flex space-x-1">
                  <button
                    onClick={() => removeWidget(widget.id)}
                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              <div className="h-full">
                {renderWidget(widget)}
              </div>
            </motion.div>
          ))}
          
          {/* Add Widget Placeholder in Edit Mode */}
          {editMode && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50"
              onClick={() => setShowWidgetLibrary(true)}
            >
              <div className="text-center">
                <PlusIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Add New Widget</p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Widget Library Modal */}
        <AnimatePresence>
          {showWidgetLibrary && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setShowWidgetLibrary(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Widget Library</h2>
                    <button
                      onClick={() => setShowWidgetLibrary(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <XMarkIcon className="w-6 h-6 text-gray-500" />
                    </button>
                  </div>
                  <p className="text-gray-600 mt-2">Choose widgets to add to your dashboard</p>
                </div>
                
                <div className="p-6 overflow-y-auto max-h-96">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableWidgets.map((widget) => (
                      <motion.div
                        key={widget.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:shadow-md"
                        onClick={() => addWidgetToDashboard(widget)}
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <widget.icon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{widget.name}</h3>
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              {widget.category}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{widget.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

// Icon mapping for safe rendering
const IconMap = {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  FireIcon,
  ChartBarIcon,
  Squares2X2Icon,
  BellIcon,
  BuildingOfficeIcon,
  UsersIcon,
  AcademicCapIcon,
  CircleStackIcon
};

// Safe icon renderer
const renderIcon = (iconName, className = "w-5 h-5") => {
  if (!iconName) return <ShieldCheckIcon className={className} />;
  
  // If it's already a component function
  if (typeof iconName === 'function') {
    const IconComponent = iconName;
    return <IconComponent className={className} />;
  }
  
  // If it's a string name, look it up in the map
  if (typeof iconName === 'string') {
    const IconComponent = IconMap[iconName];
    return IconComponent ? <IconComponent className={className} /> : <ShieldCheckIcon className={className} />;
  }
  
  // Fallback
  return <ShieldCheckIcon className={className} />;
};

// Widget Components
const KPICard = ({ widget, data }) => {
  return (
    <div className="p-6 h-full flex flex-col justify-between">
      <div className="flex items-center justify-between mb-4">
        {renderIcon(widget?.icon, "w-8 h-8 text-blue-600")}
        <span className="text-xs text-gray-500">{widget?.category || "N/A"}</span>
      </div>
      <div>
        <p className="text-3xl font-bold text-gray-900 mb-1">
          {data ? (data.value || data.total || data.percentage || data.compliance_score || "N/A") : "Loading..."}
        </p>
        <p className="text-sm font-medium text-gray-600 mb-2">{widget?.name || "Unknown Widget"}</p>
        <p className="text-xs text-gray-500">{widget?.description || ""}</p>
      </div>
    </div>
  );
};

const LineChartWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Chart"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data?.trend_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const BarChartWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Chart"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data?.chart_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const PieChartWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Chart"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data?.pie_data || []}
              cx="50%"
              cy="50%"
              outerRadius={60}
              fill="#3b82f6"
              dataKey="value"
            >
              {data?.pie_data?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || "#3b82f6"} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const DonutChartWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Chart"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data?.donut_data || []}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={60}
              fill="#3b82f6"
              dataKey="value"
            >
              {data?.donut_data?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || "#3b82f6"} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const GaugeWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full flex flex-col items-center justify-center">
      <div className="flex items-center justify-between w-full mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Gauge"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 100 50" className="w-full h-full">
          <path
            d="M 10 40 A 30 30 0 0 1 90 40"
            stroke="#e5e7eb"
            strokeWidth="4"
            fill="none"
          />
          <path
            d="M 10 40 A 30 30 0 0 1 90 40"
            stroke="#3b82f6"
            strokeWidth="4"
            fill="none"
            strokeDasharray={`${(data?.percentage || 0) * 1.26} 126`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-gray-900">
            {data?.percentage || 0}%
          </span>
        </div>
      </div>
    </div>
  );
};

const TableWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Table"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <DataTable
        columns={(data?.headers || []).map((header, colIndex) => ({
          id: `col-${colIndex}`,
          header,
          enableSorting: false,
          cell: ({ row }) => <span>{row.original[colIndex]}</span>,
        }))}
        data={data?.rows || []}
        getRowId={(r, index) => String(index)}
        emptyMessage="No data available"
        enableColumnVisibility={false}
        maxBodyHeight="16rem"
      />
    </div>
  );
};

const HeatmapWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Heatmap"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart data={data?.heatmap_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="x" name="Likelihood" />
            <YAxis dataKey="y" name="Impact" />
            <Tooltip />
            <Scatter dataKey="z" fill="#3b82f6" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const ProgressBarWidget = ({ widget, data }) => {
  return (
    <div className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{widget?.name || "Progress"}</h3>
        {renderIcon(widget?.icon, "w-5 h-5 text-gray-400")}
      </div>
      <div className="space-y-4">
        {data?.progress_items?.map((item, index) => (
          <div key={index} className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">{item.label}</span>
              <span className="text-sm text-gray-500">{item.value}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${item.value}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InteractiveDashboard;